export interface RetryWithBackoffOptions {
    /** The operation to retry. Rejecting triggers the next attempt. */
    attempt: () => Promise<void>
    /** Synchronous online check, consulted before waiting out each backoff delay. */
    isOnline: () => boolean
    /** Subscribe to "back online" events. Returns an unsubscribe function. */
    subscribeOnline: (onOnline: () => void) => () => void
    /**
     * Delay before each retry, in ms. Length = number of retries (total
     * attempts = backoffMs.length + 1). Default: [2_000, 4_000, 8_000, 16_000]
     * — spacing ≥2s clears AuthRuntime's singleFlight 2s refreshCooldownMs.
     */
    backoffMs?: number[]
}

const DEFAULT_BACKOFF_MS = [2_000, 4_000, 8_000, 16_000]

/**
 * Retry `attempt` with exponential backoff. Each backoff wait resolves early
 * if `subscribeOnline` fires while online — a wait never blocks longer than
 * its slot once connectivity returns. Rejects with the last error once all
 * attempts are exhausted.
 */
export async function retryWithBackoff(options: RetryWithBackoffOptions): Promise<void> {
    const backoffMs = options.backoffMs ?? DEFAULT_BACKOFF_MS
    let lastError: unknown

    for (let attemptIndex = 0; attemptIndex <= backoffMs.length; attemptIndex += 1) {
        try {
            await options.attempt()
            return
        } catch (err) {
            lastError = err
            if (attemptIndex === backoffMs.length) {
                break
            }
            await waitForDelayOrOnline(backoffMs[attemptIndex], options)
        }
    }

    throw lastError
}

function waitForDelayOrOnline(
    delayMs: number,
    options: Pick<RetryWithBackoffOptions, 'isOnline' | 'subscribeOnline'>,
): Promise<void> {
    return new Promise(resolve => {
        let settled = false
        const finish = () => {
            if (settled) {
                return
            }
            settled = true
            clearTimeout(timer)
            unsubscribe()
            resolve()
        }
        const timer = setTimeout(finish, delayMs)
        const unsubscribe = options.subscribeOnline(() => {
            if (options.isOnline()) {
                finish()
            }
        })
    })
}
